import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/urlHelper';
import { extensionSafeRequest, isExtensionInterferenceError } from '../utils/extensionSafeRequest';

const AuthContext = createContext();

const API = `${getApiBaseUrl()}/api`;

// Refresh token every 20 hours (token expires in 24h)
const REFRESH_INTERVAL = 20 * 60 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  const silentRefresh = useCallback(async (currentToken) => {
    if (!currentToken) return;
    try {
      const res = await axios.post(`${API}/admin/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const newToken = res.data.access_token;
      if (newToken) {
        setToken(newToken);
        localStorage.setItem('admin-token', newToken);
      }
    } catch {
      // Token expired or invalid - logout silently
    }
  }, []);

  // Re-fetch the current user (incl. up-to-date permissions) from the server.
  // The localStorage `admin-user` is set ONLY at login, so any permission
  // change made by a super_admin would otherwise stay invisible to the user
  // until they logged out and back in. Calling this on app mount (and on
  // tab focus) makes permission updates effective on next page load.
  const refreshUser = useCallback(async (currentToken) => {
    const tok = currentToken || localStorage.getItem('admin-token');
    if (!tok) return;
    try {
      const res = await axios.get(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.data) {
        setUser(res.data);
        localStorage.setItem('admin-user', JSON.stringify(res.data));
      }
    } catch (err) {
      // 401 = token expired/invalid — keep silent; next request will retry.
      if (err?.response?.status === 401) {
        setToken(null);
        setUser(null);
        localStorage.removeItem('admin-token');
        localStorage.removeItem('admin-user');
      }
    }
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin-token');
    const savedUser = localStorage.getItem('admin-user');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      // Refresh in the background so permission changes take effect on
      // the very next render, without forcing the user to logout/login.
      refreshUser(savedToken);
    }
    setLoading(false);
  }, [refreshUser]);

  // Also refresh on tab focus — covers the case where a super_admin changes
  // perms in another tab and the operator switches back to a stale tab.
  // Throttled to once per 5 minutes to avoid spamming /me on every Alt+Tab.
  const lastFocusRefresh = useRef(0);
  useEffect(() => {
    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusRefresh.current < 5 * 60 * 1000) return;
      lastFocusRefresh.current = now;
      if (localStorage.getItem('admin-token')) refreshUser();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshUser]);

  // Setup refresh interval
  useEffect(() => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    if (token) {
      refreshIntervalRef.current = setInterval(async () => {
        await silentRefresh(token);
        // Also pull the latest user object so permission changes propagate
        // within the same session (the token refresh alone keeps the stale
        // localStorage user). Cheap call, runs every 20h.
        await refreshUser(token);
      }, REFRESH_INTERVAL);
    }
    return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
  }, [token, silentRefresh, refreshUser]);

  const login = async (username, password) => {
    // Try axios first (normal path). If it fails because of a browser
    // extension intercepting fetch/XHR (Ask Gemini, Honey, etc.), retry
    // via a Blob-URL Worker that is invisible to extensions.
    const persist = (access_token, userData, must_change_password) => {
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('admin-token', access_token);
      localStorage.setItem('admin-user', JSON.stringify(userData));
      return { success: true, must_change_password: !!must_change_password };
    };

    try {
      const response = await axios.post(`${API}/admin/auth/login`, {
        username,
        password,
      });
      const { access_token, user: userData, must_change_password } = response.data;
      return persist(access_token, userData, must_change_password);
    } catch (error) {
      // If axios threw because the extension hijacked fetch/XHR (typical
      // "Request object could not be cloned" or "postMessage" errors), retry
      // through the extension-safe worker channel.
      const looksLikeExtension =
        isExtensionInterferenceError(error) ||
        (error && !error.response && error.message);

      if (looksLikeExtension) {
        try {
          const safe = await extensionSafeRequest({
            url: `${API}/admin/auth/login`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
          });
          if (safe.ok && safe.data && safe.data.access_token) {
            const { access_token, user: userData, must_change_password } = safe.data;
            return persist(access_token, userData, must_change_password);
          }
          const detail = (safe.data && safe.data.detail) || `HTTP ${safe.status}`;
          return { success: false, error: detail };
        } catch (workerErr) {
          console.error('Extension-safe login also failed:', workerErr);
          return {
            success: false,
            error: 'Uma extensão do navegador está bloqueando o login. Desative a extensão "Ask Gemini" (ou similar) ou use o modo anônimo / outro navegador.',
          };
        }
      }

      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao fazer login',
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin-user');
  };

  const getAuthHeader = () => {
    if (!token) return {};
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const hasPermission = (page, minLevel = 'view') => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const levelOrder = { none: 0, view: 1, edit: 2, full: 3 };
    const userLevel = user.permissions?.[page] || 'none';
    return (levelOrder[userLevel] || 0) >= (levelOrder[minLevel] || 0);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      refreshUser,
      getAuthHeader,
      hasPermission,
      isAuthenticated: !!token
    }}>
      {children}
    </AuthContext.Provider>
  );
};